import logger from './logger';
import * as eventManager from './eventManager';
import { DEFAULT_LOCALE_CODE, LOCALES } from './constants/locales';
import NavigationService from './NavigationService.js';
// Import shared utility functions
import {
    normalizeLocaleCode,
    findLocaleByCode,
    generateLocalePath,
    getCurrentLocaleCode as getLocaleCode,
    LOCALE_CODE_VALIDATION_REGEX
} from './utils/localeUtils';
// Import centralized state management
import { getSupportedLocales as getLocales, setSupportedLocales } from './utils/localeState';
// Import navigation utilities from separate module
import navigation from './localeNavigation';

// Navigation functions are now available from the navigation submodule
// Re-export them for backward compatibility
export const buildUrl = navigation.buildUrl;
export const buildUrlFrom = navigation.buildUrlFrom;
export const navigate = navigation.navigate;
export const navigateTo = navigation.navigateTo;
export const switchToLocale = navigation.switchToLocale;

// =============================================================================
// PUBLIC API FUNCTIONS
// =============================================================================

/**
 * @deprecated Use the getContactSalesUrl() function inside chat.js instead.
 */
export function getContactSalesUrlWithCurrentLocale() {
    // Code was moved to chat.js
    return window.disw.chat.getContactSalesUrl();
}

/**
 * Initialize the module with supported locales
 *
 * Important: By default, ALL locales are supported before initialization.
 *
 * Usage patterns:
 * - `initializeLocales(['en-US', 'de-DE', 'fr-FR'])` - Restrict to only these specific locales
 * - `initializeLocales(true)` - Explicitly enable all supported locales (same as default)
 * - `initializeLocales()` or `initializeLocales(false)` - No change (keeps default: all locales)
 *
 * Note: The boolean `true` parameter is mainly useful for development environments
 * where you want to explicitly document that all locales should be available.
 *
 * @param {string[]|boolean} locales - Array of specific locale codes to restrict to, or true to explicitly enable all locales
 */
export function initializeLocales(locales) {
    if (locales === true) {
        // Boolean true means use all supported locales from the constants file
        // This is useful for development environments where you want full locale support
        // without having to maintain a hardcoded list
        setSupportedLocales(LOCALES.map(l => l.code));
    } else if (locales && Array.isArray(locales)) {
        // Guard against empty array - that would disable ALL locales!
        if (locales.length === 0) {
            logger.warn('initializeLocales: Empty array passed - this would disable all locales! Keeping default behavior (all locales enabled).');
            return; // Keep existing supportedLocales unchanged
        }

        const newSupportedLocales = locales.reduce((accum, localeCode) => {
            // Validate locale code format using BCP 47 pattern
            if (!LOCALE_CODE_VALIDATION_REGEX.test(localeCode)) {
                logger.warn(`Invalid locale code format: ${localeCode}`);
                return accum; // Skip invalid locale codes
            }

            // or if a variation of it exists (e.g., sr-RS → sr-Latn-RS).
            const matchingLocale = findLocaleByCode(localeCode);

            if (matchingLocale) {
                if (!accum.includes(matchingLocale.code)) {
                    accum.push(matchingLocale.code);
                }
            }

            return accum;

        }, []);

        setSupportedLocales(newSupportedLocales);
    }

    // Emit LOCALES event to notify components that locales are ready
    // Only emit if eventManager is available (may not be in test environments)
    if (eventManager && eventManager.emit && eventManager.events) {
        eventManager.emit(eventManager.events.LOCALES, {
            locales: {
                getCurrentLocaleCode,
                getAllLocales,
                getSupportedLocales,
                getSupportedLanguages,
                findLocaleByCode,
                generateLocalePath,
                getDefaultLocaleCode,
                navigation,
                switchLocale,
                getDefaultLocale,
                getContactSalesUrlWithCurrentLocale,
            },
            didInitLocales: true
        });
    }
}

/**
 * Get all available locales
 * @returns {Object[]} Array of all locale objects
 */
export function getAllLocales() {
    return LOCALES;
}

/**
 * Get the current locale code from URL path or browser language
 * @returns {string} The current locale code or default if not supported
 */
export function getCurrentLocaleCode() {
    return getLocaleCode();
}


/**
 * Get the default locale code
 * @returns {string} The default locale code
 */
export function getDefaultLocaleCode() {
    return DEFAULT_LOCALE_CODE;
}

/**
 * Normalize a BCP 47 locale code by removing script subtags
 * Converts BCP 47 3-part locales (language-script-region) to 2-part (language-region)
 * Examples: sr-Latn-RS → sr-RS, zh-Hans-CN → zh-CN, zh-Hant-TW → zh-TW
 * @param {string} localeCode - The locale code to normalize
 * @returns {string} The normalized locale code
 */
export { normalizeLocaleCode };

/**
 * Get the list of supported locales
 * @returns {string[]} Array of supported locale codes
 */
export function getSupportedLocales() {
    return getLocales();
}

/**
 * Get supported language objects (filtered from master list)
 * Returns only the Language objects for locales that passed initializeLocales() validation
 * This ensures the UI components only show locales that were properly validated and initialized
 * Preserves the order from initializeLocales() for consistent UI ordering
 * @returns {Object[]} Array of supported locale objects only
 */
export function getSupportedLanguages() {
    // Preserve order from supportedLocales array (from initializeLocales)
    const supportedLocales = getLocales();
    return supportedLocales.map(code => LOCALES.find(locale => locale.code === code)).filter(Boolean);
}


// =============================================================================
// DEPRECATED FUNCTIONS (Maintained for Backward Compatibility)
// =============================================================================

/**
 * @deprecated Use generateLocalePath() and handle navigation in your application instead.
 * This function directly manipulates window.location via NavigationService which breaks SPA navigation and is not testable.
 * Navigate the window to a new locale, if the locale is supported.
 * @param {string} localeCode The locale code to navigate to.
 */
function switchLocale(localeCode) {
    logger.warn('switchLocale() is deprecated. Use generateLocalePath() and handle navigation in your application.');

    const currentLocale = getCurrentLocaleCode();
    if (currentLocale && currentLocale !== DEFAULT_LOCALE_CODE) {
        const newPath = generateLocalePath(localeCode);
        if (newPath) {
            NavigationService.navigate(NavigationService.getOrigin() + newPath);
        }
    } else {
        logger.warn('There is no locale in the URL!');
    }
}


/**
 * @deprecated Use getDefaultLocaleCode() instead. This function name is confusing as it returns a string, not an object.
 * Getter function for the default locale object.
 */
function getDefaultLocale() {
    logger.warn('getDefaultLocale() is deprecated. Use getDefaultLocaleCode() instead.');
    return DEFAULT_LOCALE_CODE;
}

// =============================================================================
// MODULE EXPORTS
// =============================================================================

// Put the exports that we want to expose to the module on default
export default {
    getCurrentLocaleCode,
    getAllLocales,
    getSupportedLocales,
    getSupportedLanguages,
    findLocaleByCode,
    generateLocalePath,
    getDefaultLocaleCode,
    normalizeLocaleCode,
    navigation,

    // Deprecated functions (kept for backward compatibility)
    switchLocale,
    getDefaultLocale,
    getContactSalesUrlWithCurrentLocale,
};
